import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import PlaceIcon from '@mui/icons-material/Place';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { BottomNavigation, BottomNavigationAction, Box, Container, Paper, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useHasNext } from '@ui/Hooks/useHasNext';
import { useScannerSupported } from '@ui/Utils/scan/scannerSupport';

interface MainContainerProps {
    navBarHeight: string;
}

export default function MainContainer(props: React.ComponentProps<typeof Container> & Readonly<MainContainerProps>) {
    const { children, navBarHeight } = props;

    const hasNext = useHasNext();
    const location = useLocation();
    const [tabIndex, setTabIndex] = useState(0);
    const scannerSupported = useScannerSupported();

    useEffect(() => {
        if (location.pathname === '/') {
            setTabIndex(0);
        } else if (location.pathname.startsWith('/device')) {
            setTabIndex(1);
        } else if (location.pathname.startsWith('/scan')) {
            setTabIndex(2);
        }
    }, [location.pathname]);

    const bottomNavHeight = '56px';

    // Styling so inactive actions appear lighter/gray while the selected one is clearer.
    const actionSx = {
        '&:not(.Mui-selected):hover': {
            '& .MuiBottomNavigationAction-label': {
                opacity: 0.75,
            },
            '& .MuiSvgIcon-root': {
                opacity: 0.75,
            },
            backgroundColor: 'action.hover',
        },
        '&.Mui-selected': {
            '& .MuiBottomNavigationAction-label': {
                fontWeight: 700,
                opacity: 1,
            },
            '& .MuiSvgIcon-root': {
                opacity: 1,
            },
            color: 'primary.main',
        },
        '& .MuiBottomNavigationAction-label': {
            fontSize: '0.7rem',
            opacity: 0.75,
            transition: 'opacity 0.2s, font-weight 0.2s',
        },
        '& .MuiSvgIcon-root': {
            opacity: 0.75,
            transition: 'opacity 0.2s',
        },
        color: 'text.secondary',
        minWidth: 0,
        padding: '6px 0',
    } as const;

    return (
        <Box sx={{ bgcolor: '#ffffff' }}>
            <Container
                disableGutters
                maxWidth="lg"
                sx={{
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    height: {
                        lg: `calc(100dvh - ${navBarHeight})`,
                        xs: `calc(100dvh - ${navBarHeight} - ${bottomNavHeight} - var(--safe-area-inset-bottom, 0px) - var(--safe-area-inset-top, 0px))`,
                    },
                    overflow: 'auto',
                }}
            >
                {children}
                {hasNext ? (
                    <Typography
                        color="primary"
                        sx={{
                            display: { lg: 'initial', xs: 'none' },
                            fontWeight: 'bold',
                            left: 3,
                            position: 'absolute',
                            top: 0,
                        }}
                        variant="caption"
                    >
                        NEXT
                    </Typography>
                ) : null}
                <Paper
                    elevation={3}
                    sx={{
                        backgroundColor: '#edf2f6',
                        borderTop: '1px solid',
                        borderTopColor: 'divider',
                        bottom: 0,
                        display: { lg: 'none', xs: 'initial' },
                        left: 0,
                        paddingBottom: 'var(--safe-area-inset-bottom, 0px)',
                        position: 'fixed',
                        right: 0,
                    }}
                >
                    <BottomNavigation
                        onChange={(event, newValue: number) => {
                            setTabIndex(newValue);
                        }}
                        showLabels
                        sx={{ bgcolor: '#edf2f6', height: bottomNavHeight }}
                        value={tabIndex}
                    >
                        <BottomNavigationAction
                            component={Link}
                            icon={<PlaceIcon />}
                            label="Places"
                            sx={actionSx}
                            to="/"
                            value={0}
                        />
                        <BottomNavigationAction
                            component={Link}
                            icon={<DevicesOtherIcon />}
                            label="Devices"
                            sx={actionSx}
                            to="/device"
                            value={1}
                        />
                        {scannerSupported && hasNext ? (
                            <BottomNavigationAction
                                component={Link}
                                icon={<QrCodeScannerIcon />}
                                label="Scan"
                                sx={actionSx}
                                to="/scan"
                                value={2}
                            />
                        ) : null}
                    </BottomNavigation>
                </Paper>
            </Container>
        </Box>
    );
}
